import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  Plus,
  Calendar,
  Clock,
  User,
  Flag,
  Trash2,
  Edit2,
  Filter,
  Download,
  RefreshCw,
  ListTodo,
  Target,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export default function Tasks() {
  const { user, isFounder } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("today");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");

  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: new Date().toISOString().split('T')[0],
    due_time: "",
    priority: "medium",
    project_id: "",
    assigned_to: "",
    send_alert: false,
    notify_assignee: true,
    notify_founder: false,
    notify_client: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, todayRes, usersRes, projectsRes] = await Promise.all([
        apiClient.get("/tasks"),
        apiClient.get("/tasks/today"),
        apiClient.get("/users"),
        apiClient.get("/projects")
      ]);
      setTasks(tasksRes.data);
      setTodayData(todayRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...form,
        project_id: form.project_id || null,
        assigned_to: form.assigned_to || null,
        send_alert: form.notify_assignee,
        notify_founder: form.notify_founder,
        notify_client: form.notify_client
      };

      if (editingTask) {
        await apiClient.put(`/tasks/${editingTask.task_id}`, data);
        toast.success("Task updated!");
      } else {
        await apiClient.post("/tasks", data);
        toast.success("Task created!");
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingTask ? "Failed to update task" : "Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      due_date: new Date().toISOString().split('T')[0],
      due_time: "",
      priority: "medium",
      project_id: "",
      assigned_to: "",
      send_alert: false,
      notify_assignee: true,
      notify_founder: false,
      notify_client: false
    });
    setEditingTask(null);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title,
      description: task.description || "",
      due_date: task.due_date || "",
      due_time: task.due_time || "",
      priority: task.priority || "medium",
      project_id: task.project_id || "",
      assigned_to: task.assigned_to || "",
      send_alert: false
    });
    setShowModal(true);
  };

  const handleStatusToggle = async (task) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    try {
      await apiClient.put(`/tasks/${task.task_id}`, { status: newStatus });
      toast.success(newStatus === "completed" ? "Task completed!" : "Task reopened");
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await apiClient.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiClient.get("/export/tasks?format=json");
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success("Tasks exported!");
    } catch (error) {
      toast.error("Failed to export tasks");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-400 bg-red-400/10";
      case "medium": return "text-yellow-400 bg-yellow-400/10";
      case "low": return "text-green-400 bg-green-400/10";
      default: return "text-gray-400 bg-gray-400/10";
    }
  };

  const getPriorityIcon = (priority) => {
    return <Flag className={`w-3 h-3 ${priority === "high" ? "text-red-400" : priority === "medium" ? "text-yellow-400" : "text-green-400"}`} />;
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== "all" && task.status !== filterStatus) return false;
    if (filterPriority !== "all" && task.priority !== filterPriority) return false;
    return true;
  });

  const myTasks = filteredTasks.filter(t => t.assigned_to === user?.user_id || (!t.assigned_to && t.created_by === user?.user_id));
  const delegatedTasks = filteredTasks.filter(t => t.assigned_by === user?.user_id && t.assigned_to !== user?.user_id);

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-6">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (<div key={i} className="h-32 skeleton"></div>))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="page-header flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <h1 className="page-title">To-Do List</h1>
          <p className="page-subtitle">Manage your tasks and delegations</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={handleExport} className="border-[#262626] flex-1 sm:flex-none text-xs sm:text-sm" data-testid="export-tasks-btn">
            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Export</span>
          </Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#FF4500] hover:bg-[#E65100] flex-1 sm:flex-none text-xs sm:text-sm" data-testid="add-task-btn">
            <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="sm:inline">Add Task</span>
          </Button>
        </div>
      </div>

      {/* Today's Summary - Responsive Grid */}
      {todayData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-2 sm:p-4 animate-fade-in-up stagger-1">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <ListTodo className="w-3 h-3 sm:w-4 sm:h-4 text-[#FF4500]" />
              <span className="text-[9px] sm:text-xs text-gray-500 uppercase truncate">Tasks</span>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{todayData.tasks?.length || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-2 sm:p-4 animate-fade-in-up stagger-2">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-[#3B82F6]" />
              <span className="text-[9px] sm:text-xs text-gray-500 uppercase truncate">Meetings</span>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{todayData.meetings?.length || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-2 sm:p-4 animate-fade-in-up stagger-3">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <Target className="w-3 h-3 sm:w-4 sm:h-4 text-[#10B981]" />
              <span className="text-[9px] sm:text-xs text-gray-500 uppercase truncate">Visits</span>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{todayData.visits?.length || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-2 sm:p-4 animate-fade-in-up stagger-4">
            <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
              <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#F59E0B]" />
              <span className="text-[9px] sm:text-xs text-gray-500 uppercase truncate">Total</span>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-white">{todayData.total_items || 0}</p>
          </div>
        </div>
      )}

      {/* Filters - Responsive */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-28 sm:w-40 bg-[#1A1A1A] border-[#262626] text-xs sm:text-sm h-8 sm:h-10">
            <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-28 sm:w-40 bg-[#1A1A1A] border-[#262626] text-xs sm:text-sm h-8 sm:h-10">
            <Flag className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={fetchData} className="text-gray-400 text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4">
          <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Tabs - Responsive */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
        <TabsList className="bg-[#1A1A1A] border border-[#262626] p-0.5 sm:p-1 flex flex-wrap h-auto">
          <TabsTrigger value="today" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[10px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
            Today {todayData?.total_items > 0 && <span className="ml-1 px-1 sm:px-1.5 py-0.5 rounded bg-white/20 text-[9px] sm:text-xs">{todayData.total_items}</span>}
          </TabsTrigger>
          <TabsTrigger value="my-tasks" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[10px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
            My Tasks {myTasks.length > 0 && <span className="ml-1 px-1 sm:px-1.5 py-0.5 rounded bg-white/20 text-[9px] sm:text-xs">{myTasks.length}</span>}
          </TabsTrigger>
          {isFounder && (
            <TabsTrigger value="delegated" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[10px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
              Delegated {delegatedTasks.length > 0 && <span className="ml-1 px-1 sm:px-1.5 py-0.5 rounded bg-white/20 text-[9px] sm:text-xs">{delegatedTasks.length}</span>}
            </TabsTrigger>
          )}
          <TabsTrigger value="all" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[10px] sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
            All
          </TabsTrigger>
        </TabsList>

        {/* Today Tab */}
        <TabsContent value="today" className="animate-fade-in">
          {/* Overdue Tasks Alert */}
          {todayData?.overdue_tasks?.length > 0 && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg animate-fade-in">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 sm:mb-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                  <h3 className="text-sm sm:text-lg font-semibold text-red-400">Urgent Attention</h3>
                </div>
                <span className="sm:ml-auto text-[10px] sm:text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded w-fit">{todayData.overdue_tasks.length} overdue</span>
              </div>
              <div className="space-y-2">
                {todayData.overdue_tasks.map((task) => (
                  <div key={task.task_id} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 sm:p-3 bg-[#171717] rounded border border-red-500/30 gap-2">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <button onClick={() => handleStatusToggle(task)} className="text-gray-400 hover:text-green-400">
                        <Circle className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-white truncate">{task.title}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500">Due: {task.due_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-6 sm:ml-0">
                      <span className={`text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${
                        task.urgency === 'critical' ? 'bg-red-500/20 text-red-400' : 
                        task.urgency === 'urgent' ? 'bg-orange-500/20 text-orange-400' : 
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {task.days_overdue}d overdue
                      </span>
                      <button onClick={() => handleEdit(task)} className="text-gray-400 hover:text-white p-1">
                        <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Today's Three Column Grid - Stack on Mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
            {/* Tasks */}
            <div className="bg-[#1A1A1A] border border-[#262626] rounded p-3 sm:p-4">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <ListTodo className="w-4 h-4 sm:w-5 sm:h-5 text-[#FF4500]" /> Today's Tasks
              </h3>
              {todayData?.tasks?.length > 0 ? (
                <div className="space-y-2">
                  {todayData.tasks.map((task) => (
                    <TaskCard key={task.task_id} task={task} onToggle={handleStatusToggle} onEdit={handleEdit} onDelete={handleDelete} getPriorityColor={getPriorityColor} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-3 sm:py-4 text-xs sm:text-sm">No tasks for today</p>
              )}
            </div>

            {/* Meetings */}
            <div className="bg-[#1A1A1A] border border-[#262626] rounded p-3 sm:p-4">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#3B82F6]" /> Meetings
              </h3>
              {todayData?.meetings?.length > 0 ? (
                <div className="space-y-2">
                  {todayData.meetings.map((meeting) => (
                    <div key={meeting.meeting_id} className="p-2 sm:p-3 bg-[#171717] rounded border border-[#262626]">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-medium text-white truncate">{meeting.agenda}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500 truncate">{meeting.project_name}</p>
                        </div>
                        <span className={`text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded flex-shrink-0 ${meeting.meeting_type === 'online' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' : 'bg-[#10B981]/20 text-[#10B981]'}`}>
                          {meeting.meeting_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-400">
                        <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {meeting.scheduled_time}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-3 sm:py-4 text-xs sm:text-sm">No meetings today</p>
              )}
            </div>

            {/* Site Visits */}
            <div className="bg-[#1A1A1A] border border-[#262626] rounded p-3 sm:p-4">
              <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981]" /> Site Visits
              </h3>
              {todayData?.visits?.length > 0 ? (
                <div className="space-y-2">
                  {todayData.visits.map((visit) => (
                    <div key={visit.visit_id} className="p-2 sm:p-3 bg-[#171717] rounded border border-[#262626]">
                      <p className="text-xs sm:text-sm font-medium text-white truncate">{visit.project_name}</p>
                      <p className="text-[10px] sm:text-xs text-gray-500 truncate">{visit.purpose}</p>
                      <div className="flex items-center gap-1 sm:gap-2 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-400">
                        <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {visit.scheduled_time}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-3 sm:py-4 text-xs sm:text-sm">No visits today</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* My Tasks Tab */}
        <TabsContent value="my-tasks" className="animate-fade-in">
          <TaskList 
            tasks={myTasks} 
            onToggle={handleStatusToggle} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            getPriorityColor={getPriorityColor}
            emptyMessage="You have no tasks"
          />
        </TabsContent>

        {/* Delegated Tab */}
        {isFounder && (
          <TabsContent value="delegated" className="animate-fade-in">
            <TaskList 
              tasks={delegatedTasks} 
              onToggle={handleStatusToggle} 
              onEdit={handleEdit} 
              onDelete={handleDelete}
              getPriorityColor={getPriorityColor}
              emptyMessage="No delegated tasks"
              showAssignee
            />
          </TabsContent>
        )}

        {/* All Tasks Tab */}
        <TabsContent value="all" className="animate-fade-in">
          <TaskList 
            tasks={filteredTasks} 
            onToggle={handleStatusToggle} 
            onEdit={handleEdit} 
            onDelete={handleDelete}
            getPriorityColor={getPriorityColor}
            emptyMessage="No tasks found"
            showAssignee
          />
        </TabsContent>
      </Tabs>

      {/* Add/Edit Task Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-lg" aria-describedby="task-description">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-[#FF4500]" />
              {editingTask ? "Edit Task" : "Add New Task"}
            </DialogTitle>
            <p id="task-description" className="text-sm text-gray-500">
              {editingTask ? "Update task details" : "Create a new task or delegate to team"}
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-400">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Task title"
                className="bg-[#171717] border-[#262626]"
                required
                data-testid="task-title-input"
              />
            </div>

            <div>
              <Label className="text-gray-400">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Task description"
                className="bg-[#171717] border-[#262626]"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Due Date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="bg-[#171717] border-[#262626]"
                />
              </div>
              <div>
                <Label className="text-gray-400">Due Time</Label>
                <Input
                  type="time"
                  value={form.due_time}
                  onChange={(e) => setForm({ ...form, due_time: e.target.value })}
                  className="bg-[#171717] border-[#262626]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Priority</Label>
                <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
                  <SelectTrigger className="bg-[#171717] border-[#262626]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="high"><span className="flex items-center gap-2"><Flag className="w-3 h-3 text-red-400" /> High</span></SelectItem>
                    <SelectItem value="medium"><span className="flex items-center gap-2"><Flag className="w-3 h-3 text-yellow-400" /> Medium</span></SelectItem>
                    <SelectItem value="low"><span className="flex items-center gap-2"><Flag className="w-3 h-3 text-green-400" /> Low</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400">Project (Optional)</Label>
                <Select value={form.project_id || "none"} onValueChange={(value) => setForm({ ...form, project_id: value === "none" ? "" : value })}>
                  <SelectTrigger className="bg-[#171717] border-[#262626]">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="none">None</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isFounder && (
              <div>
                <Label className="text-gray-400">Assign To</Label>
                <Select value={form.assigned_to || "self"} onValueChange={(value) => setForm({ ...form, assigned_to: value === "self" ? "" : value })}>
                  <SelectTrigger className="bg-[#171717] border-[#262626]">
                    <SelectValue placeholder="Assign to self" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    <SelectItem value="self">Self</SelectItem>
                    {users.filter(u => u.user_id !== user?.user_id).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role.replace("_", " ")})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Email Notification Options */}
            <div className="space-y-2">
              <Label className="text-gray-400 text-sm">Send Email Notification To:</Label>
              <div className="flex flex-wrap gap-4 p-3 bg-[#171717] rounded border border-[#262626]">
                {form.assigned_to && form.assigned_to !== user?.user_id && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.notify_assignee}
                      onChange={(e) => setForm({ ...form, notify_assignee: e.target.checked })}
                      className="rounded border-[#404040] bg-[#262626] text-[#FF4500]"
                    />
                    <span className="text-sm text-gray-300">Associate Manager</span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.notify_founder}
                    onChange={(e) => setForm({ ...form, notify_founder: e.target.checked })}
                    className="rounded border-[#404040] bg-[#262626] text-[#FF4500]"
                  />
                  <span className="text-sm text-gray-300">Me (Founder)</span>
                </label>
                {form.project_id && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.notify_client}
                      onChange={(e) => setForm({ ...form, notify_client: e.target.checked })}
                      className="rounded border-[#404040] bg-[#262626] text-[#FF4500]"
                    />
                    <span className="text-sm text-gray-300">Client</span>
                  </label>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-[#262626]">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]" data-testid="submit-task-btn">
                {submitting ? "Saving..." : editingTask ? "Update Task" : "Create Task"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Task Card Component - Mobile Optimized
function TaskCard({ task, onToggle, onEdit, onDelete, getPriorityColor }) {
  return (
    <div className={`p-2 sm:p-3 rounded border transition-all card-press ${task.status === "completed" ? "bg-[#171717]/50 border-[#262626]/50" : "bg-[#171717] border-[#262626] hover:border-[#404040]"}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        <button onClick={() => onToggle(task)} className="mt-0.5 flex-shrink-0" data-testid={`toggle-task-${task.task_id}`}>
          {task.status === "completed" ? (
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#10B981]" />
          ) : (
            <Circle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 hover:text-[#FF4500]" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className={`text-xs sm:text-sm font-medium truncate ${task.status === "completed" ? "text-gray-500 line-through" : "text-white"}`}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 truncate">{task.description}</p>
          )}
          <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2 text-[10px] sm:text-xs text-gray-400">
            {task.due_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {task.due_time}
              </span>
            )}
            {task.project_name && (
              <span className="truncate max-w-[60px] sm:max-w-[100px]">{task.project_name}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs ${getPriorityColor(task.priority)}`}>
            {task.priority?.charAt(0).toUpperCase()}
          </span>
          <button onClick={() => onEdit(task)} className="p-1 hover:bg-[#262626] rounded">
            <Edit2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
          </button>
          <button onClick={() => onDelete(task.task_id)} className="p-1 hover:bg-red-900/20 rounded">
            <Trash2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Task List Component - Shows cards on mobile, table on desktop
function TaskList({ tasks, onToggle, onEdit, onDelete, getPriorityColor, emptyMessage, showAssignee = false }) {
  if (tasks.length === 0) {
    return (
      <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 sm:p-8 text-center">
        <ListTodo className="w-8 h-8 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-2 sm:mb-3" />
        <p className="text-gray-500 text-xs sm:text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-2">
        {tasks.map((task) => (
          <div key={task.task_id} className={`bg-[#1A1A1A] border border-[#262626] rounded p-3 card-press ${task.status === "completed" ? "opacity-60" : ""}`}>
            <div className="flex items-start gap-2">
              <button onClick={() => onToggle(task)} className="flex-shrink-0 mt-0.5">
                {task.status === "completed" ? (
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-500" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium truncate ${task.status === "completed" ? "line-through text-gray-500" : "text-white"}`}>
                  {task.title}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${task.status === "completed" ? "bg-[#10B981]/20 text-[#10B981]" : task.status === "in_progress" ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-gray-700 text-gray-300"}`}>
                    {task.status?.replace("_", " ")}
                  </span>
                  {task.due_date && (
                    <span className="text-[9px] text-gray-400 flex items-center gap-1">
                      <Calendar className="w-2.5 h-2.5" /> {task.due_date}
                    </span>
                  )}
                </div>
                {showAssignee && task.assigned_to_name && (
                  <div className="flex items-center gap-1 mt-1 text-[9px] text-gray-400">
                    <User className="w-2.5 h-2.5" /> {task.assigned_to_name}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button onClick={() => onEdit(task)} className="p-1 hover:bg-[#262626] rounded">
                  <Edit2 className="w-3 h-3 text-gray-400" />
                </button>
                <button onClick={() => onDelete(task.task_id)} className="p-1 hover:bg-red-900/20 rounded">
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-[#1A1A1A] border border-[#262626] rounded overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="w-10"></th>
              <th>Task</th>
              <th>Due Date</th>
              <th>Priority</th>
              {showAssignee && <th>Assignee</th>}
              <th>Project</th>
              <th>Status</th>
              <th className="w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.task_id} className={task.status === "completed" ? "opacity-60" : ""}>
                <td>
                  <button onClick={() => onToggle(task)}>
                    {task.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-500 hover:text-[#FF4500]" />
                    )}
                  </button>
                </td>
                <td>
                  <div className={task.status === "completed" ? "line-through text-gray-500" : "text-white"}>
                    {task.title}
                  </div>
                  {task.description && (
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{task.description}</div>
                  )}
                </td>
                <td className="text-gray-400">
                  {task.due_date && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {task.due_date}
                      {task.due_time && <span className="text-gray-500 ml-1">{task.due_time}</span>}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                </td>
                {showAssignee && (
                  <td>
                    {task.assigned_to_name && (
                      <div className="flex items-center gap-1 text-gray-400">
                        <User className="w-3 h-3" />
                        {task.assigned_to_name}
                      </div>
                    )}
                  </td>
                )}
                <td className="text-gray-400">{task.project_name || "-"}</td>
                <td>
                  <span className={`badge ${task.status === "completed" ? "bg-[#10B981]/20 text-[#10B981]" : task.status === "in_progress" ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-gray-700 text-gray-300"}`}>
                    {task.status?.replace("_", " ")}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(task)} className="p-1.5 hover:bg-[#262626] rounded">
                      <Edit2 className="w-4 h-4 text-gray-400" />
                    </button>
                    <button onClick={() => onDelete(task.task_id)} className="p-1.5 hover:bg-red-900/20 rounded">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
